// Philosophies de Croissance BZ - 20 concepts pour devenir la meilleure version de soi-même
// Ces principes sont appliqués quotidiennement par nos employés pour créer une culture d'excellence

export interface Philosophie {
  id: string;
  numero: number;
  titre: string;
  categorie: 'Mindset' | 'Action' | 'Bonheur' | 'Humilité' | 'Croissance';
  description: string;
  origine?: string; // Livre, auteur, mouvement philosophique
  lienReference?: string;
  histoire: string; // Exemple concret ou anecdote
  questionReflexion: string[];
  actionConcrete: string;
  citation?: string;
  icone: string;
  couleur: string;
}

export const philosophiesCroissance: Philosophie[] = [
  // ============================================
  // CATÉGORIE: MINDSET
  // ============================================
  {
    id: 'phil-1',
    numero: 1,
    titre: 'Ça Pourrait Être Pire',
    categorie: 'Mindset',
    description: 'Face à n\'importe quel défi professionnel ou personnel, prendre du recul et réaliser qu\'il existe des situations bien plus difficiles. Cette perspective permet de relativiser et de débloquer des solutions.',
    origine: 'Livre de développement personnel (référence à confirmer)',
    histoire: 'Un directeur TI perd son plus gros client (50% revenus). Panique. Puis il se demande: "Est-ce que quelqu\'un sur la planète gérerait mieux cette situation?" Oui, probablement un entrepreneur qui a déjà rebondi 3 fois. Pourquoi? Parce qu\'il sait que chaque échec est un tremplin. La raison du blocage: peur de l\'inconnu. La solution: appeler 5 mentors, analyser les opportunités cachées. Résultat: 3 nouveaux clients en 6 mois, revenus +30%.',
    questionReflexion: [
      'Quelqu\'un sur la planète gère-t-il mieux cette situation que moi? Si oui, pourquoi?',
      'Quelle est la RAISON de mon blocage actuel?',
      'Une fois la raison identifiée, quelle est la SOLUTION évidente?'
    ],
    actionConcrete: 'Cette semaine, identifie TON défi actuel. Écris 3 raisons pour lesquelles tu bloques. Puis trouve 3 solutions concrètes pour chaque raison.',
    citation: 'La perspective transforme les obstacles en opportunités.',
    icone: 'TrendingUp',
    couleur: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'phil-2',
    numero: 2,
    titre: 'Do IT (Just Do It)',
    categorie: 'Action',
    description: 'L\'action bat la perfection. L\'analyse paralysante tue plus de projets que les mauvaises décisions. Agir imparfaitement aujourd\'hui vaut mieux que parfaitement jamais.',
    origine: 'Principe Nike adapté au développement personnel',
    histoire: 'Un employé BZ veut proposer une idée d\'automatisation IA mais attend "le moment parfait" pour présenter un POC parfait. 6 mois passent. Son collègue lance un POC imparfait en 2 semaines, itère 3 fois, et déploie la solution. Leçon: "Done is better than perfect". L\'action crée l\'information, l\'inaction crée l\'anxiété.',
    questionReflexion: [
      'Quel projet/idée je reporte depuis des semaines en attendant "le bon moment"?',
      'Quelle est la VERSION MINIMALE que je pourrais lancer CETTE SEMAINE?',
      'Qu\'est-ce qui est PIRE: échouer rapidement ou ne jamais essayer?'
    ],
    actionConcrete: 'Identifie UNE chose que tu reportes. Lance une version 20% complète dans les 48h. Itère ensuite.',
    citation: 'L\'action imparfaite bat l\'inaction parfaite.',
    icone: 'Zap',
    couleur: 'from-orange-500 to-red-500'
  },
  {
    id: 'phil-3',
    numero: 3,
    titre: 'Raison ou Solution',
    categorie: 'Mindset',
    description: 'Tu as deux choix dans la vie: chercher la raison (justification, blâme externe) ou chercher la solution (responsabilité, action). Si tu choisis la raison, tu vivras en jugeant les autres au lieu de réfléchir à ton bonheur.',
    histoire: 'Un consultant BZ rate une deadline client. Choix A: "Le client a changé les requis 10 fois, c\'est impossible!" (RAISON). Choix B: "J\'aurais dû clarifier les attentes dès le départ et mettre des jalons hebdomadaires. Prochaine fois, je fais un kickoff structuré." (SOLUTION). L\'un blâme, l\'autre grandit. Devinez qui est promu?',
    questionReflexion: [
      'Dans mon dernier échec, ai-je cherché la RAISON (justification) ou la SOLUTION?',
      'Combien de temps je passe à juger les autres vs améliorer ma situation?',
      'Quel contrôle ai-je VRAIMENT sur ma situation actuelle?'
    ],
    actionConcrete: 'Pendant 7 jours, chaque fois que tu veux blâmer quelqu\'un/quelque chose, ARRÊTE. Demande-toi: "Quelle est MA part de responsabilité? Quelle action concrète je peux prendre?"',
    citation: 'La victime cherche des raisons. Le leader cherche des solutions.',
    icone: 'Target',
    couleur: 'from-purple-500 to-pink-500'
  },
  {
    id: 'phil-4',
    numero: 4,
    titre: 'Les 5 Grands Rêves',
    categorie: 'Bonheur',
    description: 'Définis tes 5 objectifs de bonheur (non négociables) et aligne TOUTES tes actions avec eux. Si une action ne contribue pas à tes 5 rêves, c\'est du bruit. Élimine le bruit.',
    origine: 'Livre sur les objectifs de bonheur (référence à confirmer)',
    histoire: 'Un Prime BZ travaille 70h/semaine, "succès professionnel", mais divorce, santé fragile, enfants distants. Ses 5 rêves: 1) Relation épanouie, 2) Santé 20 ans, 3) Enfants fiers de lui, 4) Impact TI, 5) Liberté financière. Il réalise: 60h du travail ne servent AUCUN de ses rêves. Restructure: 45h/semaine, famille priorité, sport 4x/semaine. Résultat: Plus heureux, plus performant, promu.',
    questionReflexion: [
      'Quels sont MES 5 grands rêves (pas ceux de mes parents, conjoint, société)?',
      'Quel % de mon temps/énergie sert VRAIMENT ces 5 rêves?',
      'Quelles activités actuelles sont du BRUIT et peuvent être éliminées?'
    ],
    actionConcrete: 'MAINTENANT: Écris tes 5 grands rêves. Puis audite ta semaine dernière: % temps aligné? Liste 3 activités "bruit" à éliminer ce mois-ci.',
    citation: 'Si tout est priorité, rien n\'est priorité. Choisis tes 5 batailles.',
    icone: 'Sparkles',
    couleur: 'from-yellow-500 to-orange-500'
  },
  {
    id: 'phil-5',
    numero: 5,
    titre: 'L\'Égo Est Ton Pire Ennemi',
    categorie: 'Humilité',
    description: 'Sous-estimer ou surestimer ton égo est un jeu dangereux. L\'égo te pousse à te comparer aux autres et à "montrer" au lieu de faire pour toi-même. L\'humilité authentique bat l\'arrogance spectaculaire.',
    origine: 'Ryan Holiday - "Ego is the Enemy"',
    histoire: 'Un développeur BZ senior refuse d\'apprendre React ("Je suis expert Vue, React c\'est pour les juniors"). 2 ans plus tard, tous les projets sont en React. Il est marginalisé. Son égo l\'a tué. Pendant ce temps, son collègue "humble" apprend 3 frameworks, devient polyvalent, monte en grade. L\'égo protège l\'image, l\'humilité construit la compétence.',
    questionReflexion: [
      'Est-ce que je fais les choses pour MOI ou pour impressionner les autres?',
      'Combien de fois par jour je me compare aux autres?',
      'Quelle compétence ai-je refusé d\'apprendre par fierté?'
    ],
    actionConcrete: 'Identifie UNE compétence que tu as ignorée par ego (ex: "c\'est trop junior", "pas mon style"). Apprends-la ce mois-ci. Partage tes erreurs publiquement.',
    citation: 'L\'égo dit "Je sais". L\'humilité demande "Qu\'est-ce que je peux apprendre?"',
    icone: 'Heart',
    couleur: 'from-red-500 to-pink-500'
  },
  
  // ============================================
  // CATÉGORIE: CROISSANCE
  // ============================================
  {
    id: 'phil-6',
    numero: 6,
    titre: 'Circle of Control (Stoïcisme)',
    categorie: 'Mindset',
    description: 'Focus 100% énergie sur ce que tu CONTRÔLES (tes actions, réactions, efforts). Accepte stoïquement ce que tu ne contrôles pas (opinions des autres, résultats externes, météo).',
    origine: 'Stoïcisme - Marc Aurèle, Épictète',
    histoire: 'Un client BZ annule un projet de 200k$. Hors contrôle. L\'équipe panique 3 jours. Le Prime recentre: "On ne contrôle pas le client. On contrôle notre réaction. Actions: 1) Appeler 10 prospects cette semaine, 2) Optimiser notre pitch, 3) Améliorer notre portfolio." Résultat: 2 nouveaux clients en 1 mois. Temps perdu en panique: 0.',
    questionReflexion: [
      'Quel problème actuel m\'obsède mais que je ne contrôle PAS?',
      'Sur quoi je peux agir AUJOURD\'HUI qui est 100% sous mon contrôle?',
      'Combien d\'énergie je gaspille sur l\'incontrôlable?'
    ],
    actionConcrete: 'Liste tes 3 plus grandes préoccupations. Pour chacune, demande: "Est-ce sous mon contrôle?" Si NON, décide de l\'accepter et redirige l\'énergie vers l\'actionnable.',
    citation: 'Je ne peux pas contrôler le vent, mais je peux ajuster mes voiles. - Épictète',
    icone: 'CircleDot',
    couleur: 'from-indigo-500 to-purple-500'
  },
  {
    id: 'phil-7',
    numero: 7,
    titre: 'Growth Mindset (Mentalité de Croissance)',
    categorie: 'Croissance',
    description: 'L\'échec n\'est pas une identité, c\'est un feedback. "Je ne sais pas ENCORE" vs "Je ne suis pas bon". Le talent est un point de départ, l\'effort détermine le sommet.',
    origine: 'Carol Dweck - Psychologue Stanford',
    histoire: 'Deux employés BZ échouent une certification AWS. L\'un (Fixed Mindset): "Je ne suis pas fait pour le cloud". Abandonne. L\'autre (Growth Mindset): "Je n\'ai pas ENCORE maîtrisé, mais avec 20h de pratique ciblée..." 3 mois plus tard, certifié + architecte cloud. Même intelligence de départ, résultats radicalement différents.',
    questionReflexion: [
      'Quand j\'échoue, est-ce que je pense "Je suis nul" ou "Je n\'ai pas encore réussi"?',
      'Quelle compétence ai-je abandonnée trop vite en croyant "je ne suis pas fait pour ça"?',
      'Comment puis-je transformer mon prochain échec en apprentissage?'
    ],
    actionConcrete: 'Prochaine fois que tu échoues, écris: "3 choses que j\'ai apprises" et "Ma prochaine tentative intégrera..." Transforme l\'échec en data.',
    citation: 'Je n\'ai pas échoué. J\'ai trouvé 10,000 façons qui ne fonctionnent pas. - Edison',
    icone: 'TrendingUp',
    couleur: 'from-green-500 to-teal-500'
  },
  {
    id: 'phil-8',
    numero: 8,
    titre: 'Kaizen (1% Mieux Chaque Jour)',
    categorie: 'Croissance',
    description: 'L\'amélioration continue japonaise: 1% mieux chaque jour = 37x meilleur en 1 an. Oublie la transformation radicale, embrasse le micro-progrès quotidien.',
    origine: 'Philosophie japonaise post-WWII',
    histoire: 'Un développeur BZ veut "devenir expert IA". Objectif paralysant. Il applique Kaizen: "10 min/jour de pratique IA". Jour 1: Hello World. Jour 30: Petit chatbot. Jour 365: POC production, formation équipe, expert reconnu. Magie? Non. 1% mieux x 365 jours.',
    questionReflexion: [
      'Quel micro-progrès (1%) je peux faire AUJOURD\'HUI dans ma compétence cible?',
      'Quelle habitude de 10 min/jour transformerait ma carrière en 1 an?',
      'Pourquoi j\'attends la "grande transformation" au lieu de commencer petit?'
    ],
    actionConcrete: 'Choisis UNE compétence. Engage-toi à 10 min/jour pendant 30 jours. Tracker. Célèbre chaque micro-victoire.',
    citation: 'Les petits pas quotidiens battent les grands sauts occasionnels.',
    icone: 'ArrowUp',
    couleur: 'from-blue-500 to-indigo-500'
  },
  {
    id: 'phil-9',
    numero: 9,
    titre: 'Memento Mori (Souviens-toi de ta Mort)',
    categorie: 'Mindset',
    description: 'Tu vas mourir. Pas pour te déprimer, mais pour prioriser. Le temps est limité. Stop perdre des années sur du bruit. Vis avec urgence et intention.',
    origine: 'Stoïcisme romain',
    histoire: 'Un directeur TI BZ travaille sur des projets "intéressants mais pas critiques" depuis 5 ans. Diagnostic cancer (fausse alerte heureusement). Révélation: "Si j\'avais 2 ans à vivre, est-ce que je ferais ça?" NON. Restructure radical: Focus sur 3 projets qui l\'allument vraiment. Quitte ce qui ne sert pas sa vision. Résultat: Vie transformée, impact décuplé.',
    questionReflexion: [
      'Si j\'avais 2 ans à vivre, que changerais-je IMMÉDIATEMENT?',
      'Combien de temps je perds sur des choses que je ne ferais pas si le temps était compté?',
      'Quel projet/relation j\'ai remis à "plus tard" depuis trop longtemps?'
    ],
    actionConcrete: 'Écris ton épitaphe souhaitée. Puis audite ta vie actuelle: Est-ce aligné? Liste 3 changements pour vivre selon tes vraies priorités.',
    citation: 'La mort n\'est pas triste. C\'est vivre sans intention qui l\'est.',
    icone: 'Clock',
    couleur: 'from-gray-700 to-gray-900'
  },
  {
    id: 'phil-10',
    numero: 10,
    titre: 'Pareto 80/20 du Bonheur',
    categorie: 'Bonheur',
    description: '20% de tes activités génèrent 80% de ton bonheur. Identifie ces 20%. Double-les. Les 80% restants sont du bruit. Élimine impitoyablement.',
    origine: 'Vilfredo Pareto - Principe économique appliqué à la vie',
    histoire: 'Un Prime BZ analyse ses 3 derniers mois: Quelles activités l\'ont rendu vraiment heureux? 1) Mentorat junior (5h/semaine), 2) Projets innovation (3h/semaine), 3) Temps famille (10h/semaine). Total: 18h = 20% du temps, 80% bonheur. Le reste? Meetings inutiles, emails, politique. Action: Délègue/élimine 50% du bruit, double le temps sur les 20%.',
    questionReflexion: [
      'Quelles 3-5 activités me rendent VRAIMENT heureux (pas "devraient", mais VRAIMENT)?',
      'Combien de temps je leur consacre vs le reste?',
      'Quelle activité "bruit" je peux éliminer cette semaine pour libérer du temps?'
    ],
    actionConcrete: 'Audit de bonheur: Track 7 jours. Note activités et niveau bonheur (1-10). Identifie tes 20%. Planifie comment doubler ce temps le mois prochain.',
    citation: 'Faire plus de ce qui te rend heureux n\'est pas égoïste. C\'est intelligent.',
    icone: 'PieChart',
    couleur: 'from-pink-500 to-rose-500'
  },
  
  // ============================================
  // CATÉGORIE: ACTION & RESPONSABILITÉ
  // ============================================
  {
    id: 'phil-11',
    numero: 11,
    titre: 'Locus of Control Interne',
    categorie: 'Action',
    description: 'Tu es responsable de tes résultats. Pas la société, pas tes parents, pas "le système". Contrôle interne = pouvoir. Contrôle externe = victimisation.',
    origine: 'Julian Rotter - Psychologie',
    histoire: 'Deux consultants BZ, même formation, même client difficile. L\'un: "Ce client est impossible, je ne peux rien faire" (externe). L\'autre: "Je vais trouver un angle pour créer de la valeur malgré les contraintes" (interne). Devinez qui devient Senior?',
    questionReflexion: [
      'Combien de fois par jour je blâme des facteurs externes pour mes résultats?',
      'Quelle situation actuelle je pourrais influencer si j\'arrêtais de me voir en victime?',
      'Quel pouvoir ai-je VRAIMENT sur ma carrière/bonheur que je n\'exploite pas?'
    ],
    actionConcrete: 'Chaque fois que tu veux dire "C\'est la faute de X", STOP. Reformule: "Quelle est MA part de responsabilité et quelle action JE peux prendre?"',
    citation: 'Quand tu blâmes les autres, tu donnes ton pouvoir aux autres.',
    icone: 'Shield',
    couleur: 'from-blue-600 to-cyan-600'
  },
  {
    id: 'phil-12',
    numero: 12,
    titre: 'Gratitude Quotidienne',
    categorie: 'Bonheur',
    description: 'Ce sur quoi tu te concentres grandit. Focus sur ce qui manque = misère. Focus sur ce que tu as = abondance. 3 gratitudes/jour transforment le cerveau.',
    origine: 'Psychologie positive - Martin Seligman',
    histoire: 'Un employé BZ déprimé: "Salaire trop bas, projets ennuyants, pas de reconnaissance". Son mentor lui impose: "3 gratitudes/jour pendant 30 jours". Jour 1: Difficile. Jour 15: Plus facile. Jour 30: "J\'ai un job stable, j\'apprends chaque jour, équipe géniale". Même situation, perception transformée. Puis promu car attitude positive infectieuse.',
    questionReflexion: [
      'Quelles 3 choses je tiens pour acquises mais qui sont des privilèges?',
      'Combien de temps je passe à me plaindre vs apprécier?',
      'Comment ma vie changerait si je me concentrais sur l\'abondance au lieu de la rareté?'
    ],
    actionConcrete: 'Chaque matin, écris 3 gratitudes (spécifiques, pas génériques). Fais-le 30 jours. Observe comment ton mindset change.',
    citation: 'Ce n\'est pas le bonheur qui rend reconnaissant. C\'est la gratitude qui rend heureux.',
    icone: 'Sun',
    couleur: 'from-yellow-400 to-orange-400'
  },
  {
    id: 'phil-13',
    numero: 13,
    titre: 'Ikigai (Raison d\'Être)',
    categorie: 'Bonheur',
    description: 'L\'intersection de: 1) Ce que tu aimes, 2) Ce en quoi tu es bon, 3) Ce dont le monde a besoin, 4) Ce pourquoi tu peux être payé. Trouve ton Ikigai = vie épanouie.',
    origine: 'Philosophie japonaise d\'Okinawa (zone bleue)',
    histoire: 'Un développeur BZ excellent techniquement mais malheureux. Découvre son Ikigai: 1) Aime enseigner, 2) Bon en simplification complexe, 3) Monde a besoin de formation IA accessible, 4) BZ paie pour ça. Devient formateur interne + consultant IA. Même salaire, 10x bonheur.',
    questionReflexion: [
      'Quelle activité me fait perdre la notion du temps (zone flow)?',
      'En quoi suis-je naturellement doué (feedback des autres)?',
      'Quel impact je veux avoir sur le monde avant de mourir?'
    ],
    actionConcrete: 'Dessine ton diagramme Ikigai (4 cercles). Remplis honnêtement. Identifie l\'intersection. Planifie comment orienter 20% de ton temps vers ton Ikigai ce trimestre.',
    citation: 'Trouve ce que tu aimes faire et tu ne travailleras jamais un jour de ta vie.',
    icone: 'Compass',
    couleur: 'from-teal-500 to-green-500'
  },
  {
    id: 'phil-14',
    numero: 14,
    titre: 'Feedback Loop (Boucle de Feedback)',
    categorie: 'Croissance',
    description: 'Cherche activement le feedback brutal pour grandir plus vite. "Comment j\'aurais pu faire 10x mieux?" Les meilleurs cherchent la vérité inconfortable, les moyens évitent.',
    origine: 'Principes de Ray Dalio - Bridgewater',
    histoire: 'Deux Primes BZ après un projet. L\'un évite le feedback ("Ça s\'est bien passé, passons au suivant"). L\'autre organise un post-mortem brutal: "Qu\'est-ce qui a été nul? Qu\'est-ce que j\'ai raté?" Client + équipe honnêtes: "Communication floue, délais irréalistes". Il corrige. 6 mois plus tard, projets 2x plus fluides.',
    questionReflexion: [
      'Quand ai-je demandé un feedback VRAIMENT honnête pour la dernière fois?',
      'Est-ce que je défends mes erreurs ou est-ce que je les analyse pour grandir?',
      'Qui dans mon entourage me dirait la vérité brutale si je demandais?'
    ],
    actionConcrete: 'Demande à 3 personnes (patron, pair, junior): "Si tu pouvais améliorer UNE chose chez moi, ce serait quoi? Sois brutal." Écoute sans défendre. Remercie. Agis.',
    citation: 'Le feedback inconfortable est le petit-déjeuner des champions.',
    icone: 'RefreshCw',
    couleur: 'from-purple-500 to-indigo-500'
  },
  {
    id: 'phil-15',
    numero: 15,
    titre: 'Anti-Fragilité (Nassim Taleb)',
    categorie: 'Croissance',
    description: 'Ne sois pas juste résilient (résister au chaos). Sois anti-fragile (grandir grâce au chaos). Les difficultés te rendent plus fort si tu sais les utiliser.',
    origine: 'Nassim Taleb - "Antifragile"',
    histoire: 'Crise COVID: Certains consultants BZ paniquent (fragile). D\'autres survivent (résilient). Les anti-fragiles? Ils apprennent Teams/Zoom/remote, créent des offres digitales, doublent leurs clients. Le chaos était un accélérateur, pas un obstacle.',
    questionReflexion: [
      'Mes dernières difficultés m\'ont-elles brisé, laissé intact ou rendu plus fort?',
      'Comment puis-je transformer mon prochain challenge en opportunité de croissance?',
      'Quelle compétence puis-je développer qui me rendra anti-fragile?'
    ],
    actionConcrete: 'Identifie TON prochain chaos probable (changement techno, réorg, etc.). Prépare-toi pour en sortir PLUS FORT: Quelle compétence développer? Quel réseau bâtir?',
    citation: 'Le vent éteint la bougie mais attise le feu. Sois le feu.',
    icone: 'Flame',
    couleur: 'from-red-500 to-orange-500'
  },

  // ============================================
  // CATÉGORIE: HUMILITÉ & SAGESSE
  // ============================================
  {
    id: 'phil-16',
    numero: 16,
    titre: 'Dunning-Kruger (Connais ton Ignorance)',
    categorie: 'Humilité',
    description: 'Les incompétents surestiment leur compétence. Les experts sous-estiment la leur. Plus tu sais, plus tu réalises que tu ne sais rien. L\'humilité intellectuelle est un super-pouvoir.',
    origine: 'Effet psychologique - Dunning & Kruger',
    histoire: 'Un junior BZ après 6 mois: "Je connais React". Un senior après 10 ans: "Je commence à comprendre React". Le junior ne sait pas ce qu\'il ne sait pas (pic de confiance). Le senior a vu 10000 edge cases (vallée d\'humilité). Qui progresse plus vite? Le senior, car il sait qu\'il peut apprendre.',
    questionReflexion: [
      'Dans quel domaine je me crois expert mais je suis probablement sur le "pic d\'illusion"?',
      'Est-ce que je dis plus souvent "Je sais" ou "Je ne sais pas encore"?',
      'Quelle expertise ai-je évité d\'approfondir car je pensais "déjà tout savoir"?'
    ],
    actionConcrete: 'Choisis TON domaine d\'expertise. Trouve 3 choses que tu ignorais complètement. Célèbre ton ignorance = opportunité d\'apprendre.',
    citation: 'Je sais que je ne sais rien. - Socrate',
    icone: 'Brain',
    couleur: 'from-indigo-500 to-blue-500'
  },
  {
    id: 'phil-17',
    numero: 17,
    titre: 'Second Order Thinking (Pense 2-3 Coups à l\'Avance)',
    categorie: 'Mindset',
    description: 'Penser premier ordre: "Cette action donne quoi?" Penser second ordre: "Et après? Et les conséquences des conséquences?" Les maîtres jouent aux échecs, pas aux dames.',
    origine: 'Ray Dalio, Charlie Munger',
    histoire: 'BZ doit choisir: Projet lucratif immédiat (100k$) vs projet R&D IA (0$ court terme). Pensée 1er ordre: "100k$ évident". Pensée 2nd ordre: "R&D → compétence IA → 10 projets futurs → 1M$ + différenciation". Ils choisissent R&D. 1 an plus tard, leaders IA au Québec.',
    questionReflexion: [
      'Quelle décision récente ai-je prise pour un gain court terme qui nuit long terme?',
      'Si je choisis X aujourd\'hui, quelles seront les conséquences dans 1 an? 5 ans?',
      'Est-ce que je joue le jeu court (réactions) ou le jeu long (stratégie)?'
    ],
    actionConcrete: 'Prochaine décision importante, demande: "Et après? Et après ça? Et après ça?" Pense 3 niveaux profond. Décide avec le 3e niveau en tête.',
    citation: 'Les amateurs pensent tactique. Les pros pensent stratégie.',
    icone: 'GitBranch',
    couleur: 'from-cyan-500 to-blue-500'
  },
  {
    id: 'phil-18',
    numero: 18,
    titre: 'Via Negativa (Soustraire > Ajouter)',
    categorie: 'Action',
    description: 'Le progrès n\'est pas toujours "faire plus". Souvent, c\'est "faire moins mais mieux". Élimine les mauvaises habitudes, les projets zombies, les relations toxiques. La soustraction est sous-estimée.',
    origine: 'Nassim Taleb, Minimalisme',
    histoire: 'Un directeur BZ a 15 projets en parallèle. Tous avancent lentement. Un mentor le challenge: "Et si tu en abandonnais 10 et dominerais 5?" Résistance. Il essaie. Les 5 projets accélèrent, qualité 3x meilleure, équipe moins stressée. Moins = Plus.',
    questionReflexion: [
      'Quel projet/engagement je pourrais ARRÊTER et personne ne remarquerait (ou serait soulagé)?',
      'Quelle mauvaise habitude, si éliminée, transformerait ma vie?',
      'Qu\'est-ce que je fais par obligation/culpabilité mais qui ne sert rien?'
    ],
    actionConcrete: 'Liste 10 activités actuelles. Identifie 3 à ÉLIMINER complètement ce mois-ci. Observe comment ta performance sur le reste s\'améliore.',
    citation: 'La perfection est atteinte non pas quand il n\'y a plus rien à ajouter, mais quand il n\'y a plus rien à retirer. - Saint-Exupéry',
    icone: 'Minus',
    couleur: 'from-gray-600 to-gray-800'
  },
  {
    id: 'phil-19',
    numero: 19,
    titre: 'Skin in the Game (Avoir la Peau dans le Jeu)',
    categorie: 'Action',
    description: 'Conseiller sans risque = opinions sans valeur. Agir avec tes propres ressources/réputation en jeu = crédibilité. Fais ce que tu prêches. Mange ta propre cuisine.',
    origine: 'Nassim Taleb',
    histoire: 'Deux consultants BZ recommandent une techno. L\'un: "Vous devriez migrer vers Kubernetes". L\'autre: "On va migrer NOTRE infrastructure d\'abord, on vous montrera les résultats, puis on vous aide". Qui est crédible? Celui qui risque sa peau.',
    questionReflexion: [
      'Est-ce que je conseille des choses que je ne ferais pas moi-même?',
      'Quelle décision récente ai-je prise sans "risquer ma peau"?',
      'Comment puis-je aligner mes conseils avec mes actions?'
    ],
    actionConcrete: 'Prochaine recommandation que tu fais (client, équipe, famille): Demande-toi "Est-ce que JE le ferais avec MON argent/temps/réputation?" Si NON, tais-toi ou change de recommandation.',
    citation: 'Ne me dis pas ce que tu penses. Montre-moi ton portfolio.',
    icone: 'DollarSign',
    couleur: 'from-green-600 to-emerald-600'
  },
  {
    id: 'phil-20',
    numero: 20,
    titre: 'Amor Fati (Aimer son Destin)',
    categorie: 'Mindset',
    description: 'N\'accepte pas juste ton destin. AIME-LE. Chaque échec, chaque douleur, chaque obstacle est exactement ce dont tu avais besoin pour devenir qui tu es. Embrasse tout.',
    origine: 'Friedrich Nietzsche, Stoïcisme',
    histoire: 'Un Prime BZ perd son plus gros client, puis son emploi lors d\'une restructuration. Dévastateur. Mais il applique Amor Fati: "Et si c\'était exactement ce qu\'il me fallait?" Il lance sa boîte, devient entrepreneur, vie transformée. 5 ans plus tard: "Perdre ce job était le meilleur cadeau de ma vie". Il ne l\'a pas juste accepté. Il l\'a aimé rétrospectivement.',
    questionReflexion: [
      'Quel "pire moment" de ma vie était en fait un cadeau déguisé?',
      'Et si mes challenges actuels étaient exactement ce dont j\'ai besoin pour grandir?',
      'Comment puis-je transformer ma résistance au présent en acceptation aimante?'
    ],
    actionConcrete: 'Identifie TON pire échec passé. Écris 5 façons dont il t\'a rendu meilleur/plus sage. Puis regarde ton défi actuel: "Et si c\'était parfait?"',
    citation: 'Mon destin est d\'aimer mon destin. - Nietzsche',
    icone: 'Heart',
    couleur: 'from-rose-500 to-red-500'
  },
  
  // ============================================
  // NOUVEAUX CONCEPTS 2026 (Complètent l'Égo)
  // ============================================
  {
    id: 'phil-21',
    numero: 21,
    titre: 'La Force de Demander de l\'Aide',
    categorie: 'Humilité',
    description: 'Demander de l\'aide n\'est PAS une faiblesse, c\'est la preuve ultime de compétence. L\'égo dit "Je dois savoir". L\'humilité dit "Je veux apprendre". Ceux qui demandent de l\'aide grandissent 10x plus vite.',
    origine: 'Principe de croissance accélérée',
    histoire: 'Deux développeurs BZ face à un bug complexe. Le premier (égo): "Je vais trouver seul, je suis senior". 3 jours perdus, solution bancale. Le second (humilité): "Je ne sais pas, je demande à l\'expert". 20 minutes, solution élégante + il apprend. Qui est plus compétent? Celui qui optimise pour l\'EGO ou pour le RÉSULTAT? 6 mois plus tard, le second est devenu l\'expert car il a demandé 100 fois.',
    questionReflexion: [
      'Combien de temps je perds à chercher seul par fierté au lieu de demander en 5 min?',
      'Qui dans mon entourage sait déjà ce que je galère à apprendre?',
      'Est-ce que je préfère paraître intelligent ou DEVENIR intelligent?'
    ],
    actionConcrete: 'Cette semaine, identifie UN problème où tu bloques. Au lieu de chercher seul, demande à 3 personnes: "Comment TU ferais ça?" Observe la vitesse de résolution et ce que tu apprends.',
    citation: 'Les personnes les plus compétentes sont celles qui savent exactement à qui demander.',
    icone: 'Users',
    couleur: 'from-blue-500 to-purple-500'
  },
  {
    id: 'phil-22',
    numero: 22,
    titre: 'L\'Incompétence Consciente (Le Pouvoir du "Je Ne Sais Pas")',
    categorie: 'Humilité',
    description: 'Il y a 4 étapes de compétence: 1) Incompétence inconsciente (tu ne sais pas que tu ne sais pas), 2) Incompétence consciente (tu sais que tu ne sais pas) ← POUVOIR ICI, 3) Compétence consciente, 4) Maîtrise. Reconnaître ton incompétence = premier pas vers l\'excellence.',
    origine: 'Modèle des 4 stades d\'apprentissage - Psychologie cognitive',
    histoire: 'Un nouveau consultant BZ prétend tout savoir devant un client (incompétence inconsciente). Désastre. Son mentor lui apprend: "Dire JE NE SAIS PAS est un super-pouvoir". Projet suivant, le client demande quelque chose de complexe. Le consultant: "Excellente question. Je ne connais pas la réponse exacte, mais je vais consulter notre expert et revenir dans 2h avec la meilleure solution." Client impressionné par l\'honnêteté. Résultat: Confiance +1000%, contrat étendu.',
    questionReflexion: [
      'Combien de fois j\'ai prétendu savoir pour "sauver la face" alors que je ne savais pas?',
      'Quelle compétence j\'évite d\'apprendre parce que je ne veux pas paraître débutant?',
      'Comment ma carrière changerait si je remplaçais "Je sais" par "Je ne sais pas ENCORE"?'
    ],
    actionConcrete: 'Pendant 7 jours, chaque fois que tu ne sais pas quelque chose, dis-le CLAIREMENT: "Je ne sais pas, mais je vais trouver." Compte combien de fois. Observe comment les gens te respectent PLUS, pas moins.',
    citation: 'Dire "Je ne sais pas" est la phrase la plus puissante pour quelqu\'un qui veut apprendre.',
    icone: 'BookOpen',
    couleur: 'from-purple-500 to-pink-500'
  }
];

// Statistiques globales
export const statsPhilosophie = {
  totalConcepts: 22,
  categories: {
    Mindset: 4,
    Action: 4,
    Bonheur: 4,
    Humilité: 5,
    Croissance: 5
  },
  origines: [
    'Livres de développement personnel',
    'Philosophie stoïcienne',
    'Psychologie moderne',
    'Sagesse japonaise',
    'Entrepreneurs visionnaires'
  ]
};





